// 고른 언어 하나 — **화면 전부가 같은 값을 본다**
//
// 이 훅이 생기기 전에는 화면 뿌리 다섯(진입·작전 구성 셸·공격 열람·열람 껍데기·계측
// 프로브)이 각자 `useState(DEFAULT_LANG)` 를 들고 있었다. 그래서 EN 을 눌러도 **그 화면
// 안에서만** 영어였고, 새로고침하거나 다른 주소로 옮기면 한국어로 돌아왔다. 영어를 채워도
// 그 상태로는 쓸 수 없다 — 연맹원은 전투 중에 화면을 새로 열고, 열 때마다 자기 언어를
// 다시 골라야 한다면 그것은 고를 수단이 없는 것과 같다.
//
// ── 훅마다 상태를 주면 안 된다 ─────────────────────────────────────────────
//
// **처음에는 훅이 호출자마다 `useState` 를 하나씩 줬고 그것이 결함이었다.** 저장소는
// 공유되지만 **이미 떠 있는 형제 화면에는 전파되지 않는다.** 화면 하나만 떠 있는 자리에서는
// 안 보이다가, 열람 화면에서 드러났다 — 그 껍데기는 예약된 소리를 걷지 않으려고 공격
// 서브트리를 감춘 채 마운트해 두므로 **고정줄이 둘 동시에 산다.** ATTACK 에서 EN 을 누르고
// DEFENSE 로 옮기면 거기 스위치는 KO 였다. 한 화면이 두 언어를 주장하는 상태다.
// (2026-08-17 대리 관측.)
//
// 그래서 값을 모듈에 하나 두고 **구독으로 알린다.** 이 저장소가 신호 대상을 알리는 데
// 이미 쓰는 문법이고(`signal/live-target.ts`), 거기서와 같은 이유다 — 값이 하나뿐인 것이
// 사실이면 그 사실을 구조로 적는 편이 규율보다 오래간다.
//
// ── 저장소를 쓰는 이유 ─────────────────────────────────────────────────────
//
// 서버에 두지 않는다. 언어는 **그 사람의 것이지 방의 것이 아니고**, 방에 두면 CT 가 고른
// 언어가 연맹원 전부에게 간다. 세션에 두는 방법도 있지만 그러면 입장 화면(세션이 아직
// 없는 자리)에서 언어를 못 고른다 — 정본이 KO/EN 을 진입 화면에도 두라고 계약했다.
//
// ── 브라우저 언어를 읽지 않는다 ────────────────────────────────────────────
//
// `navigator.language` 로 첫 값을 정하는 방법이 있지만 안 쓴다. 이 연맹의 공용어는
// 한국어이고, 영어 로케일로 폰을 쓰는 한국 사람이 드물지 않다. 자동으로 골라 주면 그
// 사람은 처음 여는 화면이 영어인 이유를 모른 채 고치는 자리를 찾게 된다. **정본이 자동
// 감지를 계약한 적도 없다.** 기본은 한국어이고 사람이 고른 것만 기억한다.
//
// ── 접속 층에도 알려 준다 ──────────────────────────────────────────────────
//
// 서버가 거절할 때 보내는 코드를 사람이 읽는 문장으로 바꾸는 자리가 `access/client.ts` 인데,
// 그 함수는 React 밖이라 훅의 상태를 볼 수 없다. 값이 바뀔 때마다 그쪽에 한 번 알려 준다.

import { useSyncExternalStore } from 'react'

import { DEFAULT_LANG, type Lang, LANGS } from '../../i18n/index.ts'
import { readToken, setActiveLang, writeToken } from '../access/client.ts'

export const LANG_KEY = 'rally-ops-room.lang'

/** 저장소에 든 값이 지금도 아는 언어인가. 모르는 값이면 기본으로 되돌린다. */
function known(value: string | null): Lang | null {
  return LANGS.includes(value as Lang) ? (value as Lang) : null
}

/**
 * 지금 값. **모듈이 뜨는 순간이 아니라 처음 읽히는 순간에 저장소를 본다** — `window` 가
 * 없는 자리에서 모듈을 들이는 경로가 생겨도 그때 죽지 않게 한다.
 */
let current: Lang | null = null
const listeners = new Set<() => void>()

function snapshot(): Lang {
  if (current === null) {
    current = known(readToken(LANG_KEY)) ?? DEFAULT_LANG
    setActiveLang(current)
  }
  return current
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify)
  return () => {
    listeners.delete(notify)
  }
}

/**
 * 언어를 고른다. **저장하고, 접속 층에 알리고, 떠 있는 화면 전부를 다시 그린다.**
 *
 * 훅 밖에서도 부를 수 있게 열어 둔 이유는 없다 — 지금 부르는 곳은 아래 훅 하나다. 다만
 * 값을 바꾸는 자리가 하나라는 것이 이 파일의 성질이라 함수로 떼어 두었다.
 */
function choose(next: Lang): void {
  if (snapshot() === next) return
  current = next
  writeToken(LANG_KEY, next)
  setActiveLang(next)
  for (const notify of [...listeners]) notify()
}

export interface LangHandle {
  lang: Lang
  choose: (next: Lang) => void
}

/** 고른 언어와 그것을 바꾸는 문 하나. 부르는 화면이 몇이든 값은 하나다. */
export function useLang(): LangHandle {
  // 세 번째 인자를 같은 함수로 둔다. 서버 렌더가 없는 앱이지만, 두 값을 다르게 두면
  // 그 차이가 언젠가 화면에서 한 프레임짜리 깜빡임으로 나온다.
  const lang = useSyncExternalStore(subscribe, snapshot, snapshot)
  return { lang, choose }
}
